<template>
  <div class="d-flex flex-column gap-lg">

    <div v-if="loading">
      <KvSkeleton height="48px" width="100%" style="border-radius: 8px;" />
    </div>

    <div v-else-if="notes && notes.length">
      <KvButton size="extra-large" prepend-icon="ui-plus" fullWidth @click="openAddNote">
        {{ t('clients-notes-add_note') }}
      </KvButton>
    </div>

    <KvModal ref="addNoteModal" size="large">
      <template #title>{{ t('clients-notes-add_note') }}</template>
      <template #subtitle>{{ t('clients-notes-markdown_hint') }}</template>

      <template #body>
        <KvEditor v-model="editorValue" :placeholder="t('clients-notes-note_placeholder')" height="350px"></KvEditor>
      </template>

      <template #footer="{ toggle }">
        <KvButton variant="outlined" @click="toggle">{{ t('clients-modals-cancel') }}</KvButton>
        <KvButton prepend-icon="ui-check" @click="saveNote(toggle)" :loading="processingNote">{{ t('clients-notes-save_note') }}</KvButton>
      </template>
    </KvModal>

    <div v-if="loading" class="d-flex flex-column gap-md mt-3">
      <KvCard v-for="i in 3" :key="i" variant="tonal">
        <template #title>
          <div class="d-flex gap-md align-items-center">
            <KvSkeleton width="80px" height="24px" style="border-radius: 16px" />
            <KvSkeleton width="120px" height="14px" />
          </div>
        </template>
        <template #text>
          <KvSkeleton :rows="3" />
        </template>
      </KvCard>
    </div>

    <KvTypography v-else-if="notes && notes.length" size="large" weight="semibold" class="mt-2">
      {{ t('clients-notes-client_notes') }}
    </KvTypography>

    <div v-if="!loading && notes && notes.length > 0" class="notes-scroll-container">
      <KvScrollbar content-class="d-flex flex-column gap-md">
        <KvCard v-for="note in notes" :key="note.id" variant="tonal">
          <template #title>
            <div class="d-flex gap-md align-items-center">
              <KvChip>{{ note.author }} </KvChip>
              <KvTypography weight="medium" size="extra-small">
                <HumanDate :date="note.created_at" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf" />
              </KvTypography>
            </div>
          </template>
          <template #text>
            <div class="kv-html-content" v-html="note.content"></div>
          </template>
        </KvCard>
      </KvScrollbar>
    </div>

    <div v-else-if="!loading && notes && notes.length === 0" class="d-flex flex-column align-items-center justify-content-center gap-md h-100 text-center py-4">
      <div class="empty-state-icon d-flex align-items-center justify-content-center">
        <Icon icon-class="ui-archive" size="15" color="#c4c4c8" />
      </div>

      <div class="d-flex flex-column gap-sm">
        <KvTypography weight="regular" size="extra-large">
          {{ t('clients-notes-no_notes') }}
        </KvTypography>
        <KvTypography size="small" color="muted">
          {{ t('clients-notes-no_notes_subtitle') }}
        </KvTypography>
      </div>

      <KvButton class="mt-2" size="large" prepend-icon="ui-plus" @click="openAddNote">
        {{ t('clients-notes-create_first_note') }}
      </KvButton>
    </div>

  </div>
</template>

<script>
import KvButton from '@/components/form/kv-button.vue';
import KvModal from '@/components/modal/modal.vue'; // Verifique se o caminho é esse mesmo base no seu projeto
import KvCard from '@/components/ui/kv-card.vue';
import KvChip from '@/components/ui/kv-chip.vue';
import KvTypography from '@/components/ui/kv-typography.vue';
import KvEditor from '@/components/inputs/kv-editor.vue';
import keviApi from '@/keviApi';
import httpStatus from '@/helpers/httpStatus';
import KvScrollbar from '@/components/ui/kv-scrollbar.vue';
import HumanDate from '@/components/human-date/human-date.vue';
import KvSkeleton from "@/components/ui/kv-skeleton.vue";
import Icon from '@/components/icon/icon.vue';

export default {
  components: {
    KvButton,
    KvModal,
    KvTypography,
    KvCard,
    KvChip,
    KvEditor,
    KvScrollbar,
    HumanDate,
    KvSkeleton,
    Icon
  },
  props: {
    client: Object,
    notes: Array,
    loading: { type: Boolean, default: false }
  },
  data() {
    return {
      editorValue: {},
      processingNote: false,
    };
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    // Método para abrir o modal via Ref
    openAddNote() {
      if (this.$refs.addNoteModal) {
        this.$refs.addNoteModal.open();
      }
    },
    async saveNote(toggleModal) {
      if (!this.editorValue || !this.editorValue.html) return;

      this.processingNote = true;

      try {
        var options = keviApi.getOptions("client/add-note", "POST", {
          client_id: this.client.id,
          content: this.editorValue.html,
        });
        const response = await this.$http(options)

        if (response.status === httpStatus.OK) {
          this.notes.unshift(response.body.note);
          this.editorValue = {};

          // Usa a função toggle que veio do slot do modal para fechar
          if (typeof toggleModal === 'function') {
            toggleModal(false);
          } else {
            // Fallback caso algo dê errado com o slot
            this.$refs.addNoteModal.close();
          }

        } else {
          this.$store.dispatch(
            "notification/warning",
            this.t('clients-notes-save_error', [response.body.error]),
          );
        }

      } finally {
        this.processingNote = false;
      }
    }
  }
};
</script>

<style>
.kv-html-content * {
  margin: 0 !important;
}

.empty-state-icon {
  background-color: #f5f5f5;
  border-radius: 50%;
  width: 100px;
  height: 100px;
}

.notes-scroll-container {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
</style>